import Interweave from 'interweave'
import isEmpty from 'lodash-es/isEmpty'
import moment from 'moment'
import PropTypes from 'prop-types'
import { Fragment, useMemo } from 'react'
import ReactTooltip from 'react-tooltip'
import styled from 'styled-components'
import { isUri } from 'valid-url'
import { convertMdToHtml } from '..'
import infoIcon from '../../../assets/images/info-icon.svg'
import {
  AUDIT_TYPE,
  QUALITY_ASSESMENT_LEVELS,
  QUALITY_ASSESMENT_OPTIONS,
  REPO_TYPE,
  SEVERITY_DESCRIPTION,
  SEVERITY_ID,
  VULNERABILITY_STATUS_COLOR,
} from '../../../constants'
import Grid from '../../Grid'
import Link from '../../Link'
import Section from '../../Section'
import StackChart from '../../StackChart'
import { parseTableFormat } from './utils'

const getQualityBarDetails = type => {
  switch (type) {
    case QUALITY_ASSESMENT_LEVELS.HIGH:
      return {
        count: 85,
        color: '#4BA03E',
      }
    case QUALITY_ASSESMENT_LEVELS.MEDIUM:
      return {
        count: 50,
        color: '#D6AE1B',
      }
    case QUALITY_ASSESMENT_LEVELS.LOW:
      return {
        count: 20,
        color: '#AA4700',
      }
    default:
      return {
        count: 0,
      }
  }
}

const getFindingsSeverityDetails = (findings, severityId) => {
  let data = {},
    total = 0

  findings
    .filter(finding =>
      typeof severityId !== 'undefined' ? Number(finding.severity.value) === severityId : finding,
    )
    .forEach(finding => {
      const statusId = finding?.status?.value

      // @ts-ignore
      data[statusId] = {
        label: finding?.status?.label,
        // @ts-ignore
        count: (data[statusId]?.count || 0) + 1,
        color: VULNERABILITY_STATUS_COLOR[statusId],
      }
      total += 1
    })

  return {
    data,
    total,
  }
}

const hasSpecifications = docArray => docArray?.some(specification => specification?.type?.trim())

const SummaryContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 24px;

  @media only screen and (max-width: 1200px) {
    flex-direction: column;
    gap: 16px;
  }

  ul {
    padding-inline-start: inherit;
  }
`

const SourceCodeContainer = styled.li`
  div {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    justify-content: space-between;
    align-items: center;
  }

  @media only screen and (max-width: 1200px) {
    &:not(:last-child) {
      margin-bottom: 12px;
    }
  }
`

const AuditorContainer = styled.li`
  @media only screen and (max-width: 1200px) {
    &:not(:last-child) {
      margin-bottom: 12px;
    }
  }

  span {
    font-size: 12px;
    line-height: 22px;

    @media only screen and (max-width: 1200px) {
      margin-top: 4px;
      font-weight: 600;
      line-height: 20px;
      display: block;
      width: fit-content;
      background: #f2f2f2;
      mix-blend-mode: normal;
      border-radius: 999px;
      padding: 1px 8px;
    }
  }
`

const InfoImage = styled.img`
  width: 12px;
  height: 12px;
`

const FindingDetails = ({ findingTotal, findings, severityId }) => {
  const severityDetails = useMemo(() => getFindingsSeverityDetails(findings, severityId), [
    findings,
    severityId,
  ])

  return (
    <>
      {findingTotal}
      {
        <StackChart
          data={Object.values(severityDetails.data)}
          total={severityDetails.total}
          width={190}
        />
      }
    </>
  )
}

FindingDetails.propTypes = {
  findingTotal: PropTypes.number,
  findings: PropTypes.array,
  severityId: PropTypes.string,
}

const ExecutiveSummary = ({ report: { reportContent } }) => {
  const isNormalAudit = reportContent.auditType === AUDIT_TYPE.NORMAL || !reportContent.auditType
  const documentationQualityBarDetails = getQualityBarDetails(
    QUALITY_ASSESMENT_OPTIONS[reportContent.summary.documentationQuality]?.value,
  )
  const testQualityBarDetails = getQualityBarDetails(
    QUALITY_ASSESMENT_OPTIONS[reportContent.summary.testQuality]?.value,
  )

  const highRiskFindingsTotal = reportContent.summary?.issues?.highRisk || 0
  const mediumRiskFindingsTotal = reportContent.summary?.issues?.mediumRisk || 0
  const lowRiskFindingsTotal = reportContent.summary?.issues?.lowRisk || 0
  const infoRiskFindingsTotal = reportContent.summary?.issues?.infoRisk || 0
  const undetRiskFindingsTotal = reportContent.summary?.issues?.undetRisk || 0

  const findingsTypes = useMemo(
    () => [
      {
        label: 'High severity findings',
        total: highRiskFindingsTotal,
        severityId: SEVERITY_ID.HIGH,
      },
      {
        label: 'Medium severity findings',
        total: mediumRiskFindingsTotal,
        severityId: SEVERITY_ID.MEDIUM,
      },
      {
        label: 'Low severity findings',
        total: lowRiskFindingsTotal,
        severityId: SEVERITY_ID.LOW,
      },
      {
        label: 'Undetermined severity findings',
        total: undetRiskFindingsTotal,
        severityId: SEVERITY_ID.UNDETERMINED,
      },
      {
        label: 'Informational findings',
        total: infoRiskFindingsTotal,
        severityId: SEVERITY_ID.INFORMATIONAL,
      },
    ],
    [
      highRiskFindingsTotal,
      mediumRiskFindingsTotal,
      lowRiskFindingsTotal,
      undetRiskFindingsTotal,
      infoRiskFindingsTotal,
    ],
  )

  const allSeverityDetails = useMemo(
    () =>
      getFindingsSeverityDetails(
        reportContent?.breakdownAndAutomatedAnalysis?.vulnerabilitiesArray,
      ),
    [reportContent?.breakdownAndAutomatedAnalysis?.vulnerabilitiesArray],
  )

  // Move audit lead to beginning of list
  const auditLeadIndex = reportContent.summary.auditorArray.findIndex(
    auditor => auditor.username === reportContent.AuditLead,
  )
  if (auditLeadIndex !== -1) {
    reportContent.summary.auditorArray.unshift(
      reportContent.summary.auditorArray.splice(auditLeadIndex, 1)[0],
    )
  }

  return (
    <Section title="Executive Summary">
      <p>
        {isEmpty(reportContent.summary.ribbonText)
          ? `This ${
              isNormalAudit ? 'audit' : 'security review'
            } was prepared by Quantstamp, the leader in blockchain security. ` +
            ` This security ${
              isNormalAudit ? 'audit' : 'security review'
            } report follows a generic template.`
          : reportContent.summary.ribbonText}
      </p>
      <SummaryContainer>
        <Grid width="100%" columns="min(125px) 1fr">
          <Grid.Item fontWeight="500" justifyContent="center">
            Type
          </Grid.Item>
          <Grid.Item>{reportContent.setup.projectType}</Grid.Item>
          <Grid.Item fontWeight="500" justifyContent="center">
            Timeline
          </Grid.Item>
          <Grid.Item>
            {moment(reportContent?.summary?.timeline?.start || moment()).format('YYYY-MM-DD')}
            &nbsp;through&nbsp;
            {moment(reportContent?.summary?.timeline?.end || moment()).format('YYYY-MM-DD')}
          </Grid.Item>
          <Grid.Item fontWeight="500" justifyContent="center">
            Language
          </Grid.Item>
          <Grid.Item>
            {Object.keys(reportContent.summary.languages)
              .filter(language => reportContent.summary.languages[language])
              .join(', ')}
          </Grid.Item>
          <Grid.Item fontWeight="500" justifyContent="center">
            Methods
          </Grid.Item>
          <Grid.Item>{reportContent.summary.methods}</Grid.Item>
          {hasSpecifications(reportContent.summary.docArray) ? (
            <>
              <Grid.Item fontWeight="500" justifyContent="center">
                Specification
              </Grid.Item>
              <Grid.Item>
                {reportContent.summary.docArray
                  .filter(specification => specification?.type?.trim())
                  .map((specification, index) => (
                    <Fragment key={index}>
                      {specification.link ? (
                        <Link href={specification.link} target="_blank" rel="noreferrer">
                          {specification.type}
                        </Link>
                      ) : (
                        specification.type
                      )}
                    </Fragment>
                  ))}
              </Grid.Item>
            </>
          ) : null}
          {reportContent.summary.forkInfo ? (
            <>
              <Grid.Item fontWeight="500" justifyContent="center">
                Diff/Fork information
              </Grid.Item>
              <Grid.Item>
                <Interweave content={convertMdToHtml.makeHtml(reportContent.summary.forkInfo)} />
              </Grid.Item>
            </>
          ) : null}
          <Grid.Item fontWeight="500" justifyContent="center">
            Source Code
          </Grid.Item>
          <Grid.Item>
            <ul>
              {reportContent.setup.repos?.length
                ? reportContent.setup.repos
                    .filter(repo => !repo.hidden)
                    .map((repo, index) =>
                      repo.repoType === REPO_TYPE.REGULAR ? (
                        <SourceCodeContainer key={index}>
                          <div>
                            {repo.repoLink && isUri(repo.repoLink) ? (
                              <Link href={repo.repoLink} target="_blank" rel="noreferrer">
                                {repo.repoName || repo.repoLink}
                              </Link>
                            ) : (
                              repo.repoName || repo.repoLink || 'None'
                            )}

                            {repo.commit && repo.repoLink && isUri(repo.repoLink) ? (
                              <Link
                                href={`${repo.repoLink}/commit/${repo.commit}`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                #{repo.commit.slice(0, 7)}
                              </Link>
                            ) : (
                              `#${repo.commit?.slice(0, 7)}`
                            )}
                          </div>
                        </SourceCodeContainer>
                      ) : (
                        <SourceCodeContainer key={index}>
                          <div>
                            {repo.repoCustomLink &&
                            isUri(repo.repoCustomLink) &&
                            repo.repoCustomName ? (
                              <Link href={repo.repoCustomLink} target="_blank" rel="noreferrer">
                                {repo.repoCustomName}
                              </Link>
                            ) : (
                              repo.repoCustomName
                            )}

                            {repo.commitCustomLink &&
                            isUri(repo.commitCustomLink) &&
                            repo.commitCustomName ? (
                              <Link href={repo.commitCustomLink} target="_blank" rel="noreferrer">
                                #{repo.commitCustomName}
                              </Link>
                            ) : (
                              repo.commitCustomName
                            )}
                          </div>
                        </SourceCodeContainer>
                      ),
                    )
                : null}
            </ul>
          </Grid.Item>
          <Grid.Item fontWeight="500" justifyContent="center">
            Auditors
          </Grid.Item>
          <Grid.Item>
            <ul>
              {reportContent.summary.auditorArray.map((auditor, index) => (
                <AuditorContainer key={index}>
                  {auditor.name} <span>{auditor.role}</span>
                </AuditorContainer>
              ))}
            </ul>
          </Grid.Item>
        </Grid>
        <Grid width="100%">
          <Grid.Item fontWeight="500" justifyContent="center">
            Documentation quality
          </Grid.Item>
          <Grid.Item
            alignItems="center"
            justifyContent="space-between"
            direction="row"
            gap="10px"
            wrap="wrap"
          >
            {QUALITY_ASSESMENT_OPTIONS[reportContent.summary.documentationQuality]?.label}
            {!!documentationQualityBarDetails.count && (
              <StackChart
                data={[
                  {
                    count: documentationQualityBarDetails.count,
                    color: documentationQualityBarDetails.color,
                  },
                  { count: 100 - documentationQualityBarDetails.count },
                ]}
                total={100}
                width={100}
              />
            )}
          </Grid.Item>
          <Grid.Item fontWeight="500" justifyContent="center">
            Test quality
          </Grid.Item>
          <Grid.Item
            alignItems="center"
            justifyContent="space-between"
            direction="row"
            gap="10px"
            wrap="wrap"
          >
            {QUALITY_ASSESMENT_OPTIONS[reportContent.summary.testQuality]?.label}
            {!!testQualityBarDetails.count && (
              <StackChart
                data={[
                  {
                    count: testQualityBarDetails.count,
                    color: testQualityBarDetails.color,
                  },
                  { count: 100 - testQualityBarDetails.count },
                ]}
                total={100}
                width={100}
              />
            )}
          </Grid.Item>
          <Grid.Item fontWeight="500" justifyContent="center">
            Total Findings
          </Grid.Item>
          <Grid.Item alignItems="center" justifyContent="space-between" direction="row" wrap="wrap">
            {highRiskFindingsTotal +
              mediumRiskFindingsTotal +
              lowRiskFindingsTotal +
              infoRiskFindingsTotal +
              undetRiskFindingsTotal}
            <StackChart
              data={Object.values(allSeverityDetails.data)}
              total={allSeverityDetails.total}
              width={190}
            />
          </Grid.Item>
          {findingsTypes.map(finding => (
            <Fragment key={finding.label}>
              <Grid.Item fontWeight="500" gap="5px" direction="row" alignItems="center">
                {finding.label}
                <InfoImage src={infoIcon} alt="Info icon" data-tip="" data-for={finding.label} />
                <ReactTooltip
                  id={finding.label}
                  getContent={() => SEVERITY_DESCRIPTION[finding.severityId]}
                />
              </Grid.Item>
              <Grid.Item
                alignItems="center"
                justifyContent="space-between"
                direction="row"
                wrap="wrap"
              >
                <FindingDetails
                  findingTotal={finding.total}
                  findings={reportContent.breakdownAndAutomatedAnalysis.vulnerabilitiesArray}
                  severityId={finding.severityId}
                />
              </Grid.Item>
            </Fragment>
          ))}
        </Grid>
      </SummaryContainer>
      {reportContent.summary?.executiveFooter && (
        <div className="executive-footer-container">
          <Interweave content={parseTableFormat(reportContent.summary.executiveFooter)} />
        </div>
      )}
    </Section>
  )
}

ExecutiveSummary.propTypes = {
  report: PropTypes.object,
}

export default ExecutiveSummary
